import { FilterBoxConfig } from '@nx-fe/filter';
import { PageCategory, PageConfigWithCharts } from '../../../shared/features/ng-redux/page/page.models';
import { buildChartOptions, findPageId, patchCard, patchChart, patchPage } from './app-mapper.utils';
import { ApplicationConfigWithPages } from '../../../shared/features/ng-redux/app/appConfig.models';
import {
  DEVICE_APP_USAGE,
  DEVICE_WEBAPP_USAGE,
  DEVICE_BATTERY_USAGE,
  DEVICE_DATA_USAGE,
  DEVICE_LAST_KNOWN,
  DEVICE_SIGNAL_STRENGTH,
  FILTER_VALIDATION_NONEMPTY_SAFE_TEXT,
  SMART_BATTERY_PAGE,
  SMART_BATTERY_SPOTLIGHT,
  SMART_BATTERY_TABLE,
  SMART_BATTERY_DECOMPAGE,
  SMART_BATTERY_NEWPAGE,
  SMART_BATTERY_ALLPAGE,
  SMART_BATTERY_AGE,
  STARTING_CHARGE_LEVEL_COLORS,
  SMART_BATTERY_CYCLES,
  SMART_BATTERY_REMAINING_BATTERY_CAPACITY,
  SMART_BATTERY_CHARGE_LEVEL,
  DEVICE_BATTERY_USAGE_W,
  DEVICE_WIFI,
  DEVICE_SIGNAL_STRENGTH_W,
  DEVICE_DATA_USAGE_W,
} from './app-mapper.constants';
import {
  BarChartConfig,
  ChartTypeEnum,
  GroupedBarChartConfig,
  StackedDonutChartConfig,
  TableConfig,
  TimelineChartConfig,
} from '../../../shared/models/chart.models';

import { HighchartsConstant, QUALITY_COLORS } from '../../../../../../xs-colors';
import { InsSelectionType, TableRenderEnum } from '../../../../../../../libs/table/src/lib/table/models/table.models';
import { CONSTANTS } from '../../../shared/localizations/dictionary';

const DEFAULT_BATTERY_FILTER_BAR_CONFIG: FilterBoxConfig = {
  autoUpdate: false,
  andOnly: true,
  showReset: true,
  uniqueFields: true,
  fields: [
    {
      id: 'serialNo',
      name: 'smart_battery_serial_number_name',
      type: 'string',
      validation: FILTER_VALIDATION_NONEMPTY_SAFE_TEXT,
    },
    {
      id: 'mfgBatteryId',
      name: 'sb_spotlight_summary_battery_id_label',
      type: 'string',
      validation: FILTER_VALIDATION_NONEMPTY_SAFE_TEXT,
    },
    {
      id: 'batteryPartNumber',
      name: 'smart_battery_part_number_name',
      type: 'string',
      validation: FILTER_VALIDATION_NONEMPTY_SAFE_TEXT,
    },
    {
      id: 'batteryStatus',
      name: 'smart_battery_battery_status_name',
      type: 'selector',
    },
    {
      id: 'manufacturer',
      name: 'smart_battery_device_manufacturer_name',
      type: 'string',
      validation: FILTER_VALIDATION_NONEMPTY_SAFE_TEXT,
    },
    {
      id: 'sbManufacturer',
      name: 'smart_battery_manufacturer_name',
      type: 'string',
      validation: FILTER_VALIDATION_NONEMPTY_SAFE_TEXT,
    },
    {
      id: 'model',
      name: 'smart_battery_device_model_name',
      type: 'string',
      validation: FILTER_VALIDATION_NONEMPTY_SAFE_TEXT,
    },
    {
      id: 'deviceName',
      name: 'smart_battery_device_name',
      type: 'string',
      validation: FILTER_VALIDATION_NONEMPTY_SAFE_TEXT,
    },
    {
      id: 'deviceId',
      name: 'smart_battery_deviceId_name',
      type: 'string',
      validation: FILTER_VALIDATION_NONEMPTY_SAFE_TEXT,
    },
    // { id: 'manufactureDate', name: 'app_mapper_smart_Manufacture_Date', type: 'date' },
    { id: 'batteryCycles', name: 'smart_battery_chart_charge_cycles_name', type: 'number' },
    { id: 'batteryAge', name: 'smart_battery_chart_battery_months_days_name', type: 'number' },
    // { id: 'lastDevice', name: 'app_mapper_smart_Last_Device_ID', type: 'string', validation: FILTER_VALIDATION_NONEMPTY_SAFE_TEXT },
  ],
};

function patchSmartBatteryPage(page: PageConfigWithCharts) {
  page.pageCategory = PageCategory.BATTERYSHIFT;
  const noDataText = 'smart_battery_no_data_text';

  page.filterBarVisible = true;
  page.filterBarConfig = DEFAULT_BATTERY_FILTER_BAR_CONFIG;

  page.name = 'smart_battery_page_last_shift_name';

  page.predefinedCharts = [
    {
      category: {
        name: 'smart_battery_predefined_chart_category_name',
        icon: 'insight-icon-smartbattery',
        tooltip: 'smart_battery_predefined_charts_tooltip',
      },
      charts: [
        // buildChartOptions(page.charts, 'sbHealth', 'app_mapper_smart_Battery_Lasting_Shift'),
        buildChartOptions(page.charts, 'sbAge', 'smart_battery_chart_battery_age_name'),
        // buildChartOptions(page.charts, 'sbCyclesEx', 'app_mapper_smart_Battery_Charge_Cycles'),
        // buildChartOptions(page.charts, 'sbEstCapacityEx', 'app_mapper_smart_Remaining_Battery_Capacity'),
        buildChartOptions(page.charts, 'sbChargeLevel', 'smart_battery_chart_charge_level_name'),
      ],
    },
  ];

  patchChart(page.charts, 'sbLastUpdated', (chart: BarChartConfig) => {
    chart.cardStyle = ['topStatic', 'static'];
  });

  patchChart(page.charts, 'sbInfoText', (chart: BarChartConfig) => {
    chart.cardStyle = ['topStatic', 'static'];
    chart.fixedBelowMultiCharts = true;
    chart.fullName = 'smart_battery_latest_chart_data_message';
  });

  patchChart(page.charts, SMART_BATTERY_AGE, (chart: BarChartConfig) => {
    chart.integerOnly = true;
    chart.metric = { metricFormat: null, metricId: 'y', label: 'battery_count_label', format: null };
    chart.tag = {
      name: 'smart_battery_chart_battery_age_name',
    };
    chart.dimension = {
      dimensionId: 'label',
      label: 'common_months_label',
      noDataLabel: '{NO_DATA}',
    };
    chart.name = 'smart_battery_chart_battery_age_name';
    chart.fullName = 'smart_battery_chart_age_fullname';
    chart.noDataText = noDataText;
    chart.tooltip = {
      label:
        '`${xsI18nPipe.transform("smart_battery_chart_age_tooltip_label", xsI18nPipe.transform(this.point.label))}`',
      labelArgs: ['smart_battery_chart_age_tooltip_label', 'xsI18nPipe.transform(this.point.label)'],
      value:
        "`${xsI18nPipe.transform('smart_battery_chart_age_tooltip_value', this.point.y, ((this.point.y/this.point.batcount)*100).toFixed(2), this.point.batcount)}`",
      valueArgs: [
        'smart_battery_chart_age_tooltip_value',
        'this.point.y',
        'percentage(this.point.y, this.point.batcount)',
        'this.point.batcount',
      ],
    };
  });

  patchChart(page.charts, SMART_BATTERY_CYCLES, (chart: GroupedBarChartConfig) => {
    chart.multiChart = true;
    chart.cardStyle = ['topStatic', 'static'];
    chart.tag = { name: 'smart_battery_chart_charge_cycles_name' };
    chart.fullName = 'smart_battery_chart_charge_cycles_fullname';
    chart.name = 'smart_battery_chart_charge_cycles_name';
    chart.noDataText = noDataText;

    chart.metric = { metricFormat: null, metricId: 'y', label: 'battery_count_label', format: null };
    chart.childDimension = {
      dimensionId: 'filterIdx',
      label: 'common_date_label',
      noDataLabel: 'no_data_label',
    };
    chart.dimension = {
      dimensionId: 'cycles',
      label: 'smart_battery_chart_charge_cycles_dimension_label',
      noDataLabel: 'no_data_label',
    };
    chart.filter = { type: 'mapped', attribute: 'cycles', childAttribute: 'time' };

    chart.tooltip = {
      label: "`${xsI18nPipe.transform('smart_battery_chart_charge_cycles_tooltip_label', this.series.name)}`",
      labelArgs: ['smart_battery_chart_charge_cycles_tooltip_label', 'this.series.name'],
      pastLabel: "`${xsI18nPipe.transform('smart_battery_chart_charge_cycles_tooltip_label', this.series.name)}`",
      pastLabelArgs: ['smart_battery_chart_charge_cycles_tooltip_label', 'this.series.name'],
      value:
        "`${xsI18nPipe.transform('smart_battery_chart_charge_cycles_tooltip_value', this.y, (this.y/this.series.userOptions.count*100).toFixed(2), this.series.userOptions.count)}`",
      valueArgs: [
        'smart_battery_chart_charge_cycles_tooltip_value',
        'this.y',
        'percentage(this.y, this.series.userOptions.count)',
        'this.series.userOptions.count',
      ],
      dateTime: 'this.x',
    };

    chart.colors = [
      HighchartsConstant.defaultBucket.good,
      HighchartsConstant.defaultBucket.average,
      HighchartsConstant.defaultBucket.bad,
      HighchartsConstant.defaultBucket.nodata,
    ];

    chart.uiSettings = [
      {
        id: 'forecast',
        controlType: 'SELECTBOX',
        type: 'number',
        displayType: 'dropdown',
        name: 'smart_battery_charts_forecast_dropdown_name',
        options: [
          {
            value: 0,
            label: 'smart_battery_dropdown_current_month_label',
          },
          {
            value: 1,
            label: 'smart_battery_dropdown_current_month_and_next_1_month_label',
          },
          {
            value: 2,
            label: 'smart_battery_dropdown_current_month_and_next_2_month_label',
          },
          {
            value: 3,
            label: 'smart_battery_dropdown_current_month_and_next_3_month_label',
          },
        ],
      },
      {
        id: 'history',
        controlType: 'SELECTBOX',
        type: 'number',
        displayType: 'dropdown',
        name: 'smart_battery_charts_past_range_dropdown_name',
        options: [
          {
            value: 0,
            label: 'smart_battery_dropdown_current_month_label',
          },
          {
            value: 1,
            label: 'smart_battery_dropdown_current_month_and_previous_1_month_label',
          },
          {
            value: 2,
            label: 'smart_battery_dropdown_current_month_and_previous_2_month_label',
          },
          {
            value: 3,
            label: 'smart_battery_dropdown_current_month_and_previous_3_month_label',
          },
        ],
      },
      {
        id: 'batteryCycles',
        controlType: 'THRESHOLD',
        type: 'number',
        displayType: 'dropdown',
        name: 'app_mapper_smart_Select_the_cycles_ranges',
        options: [],
      },
      {
        id: 'predictionBeta',
        name: 'smart_battery_chart_health_prediction_engine_name',
        tooltip: 'smart_battery_chart_health_prediction_engine_tooltip',
        controlType: 'decal',
      },
    ];
  });

  patchChart(page.charts, SMART_BATTERY_REMAINING_BATTERY_CAPACITY, (chart: GroupedBarChartConfig) => {
    chart.multiChart = true;
    chart.default = true;
    chart.cardStyle = ['topStatic', 'static'];
    chart.tag = {
      name: 'smart_battery_chart_battery_capacity_name',
    };
    chart.name = 'smart_battery_chart_battery_capacity_name';
    chart.fullName = 'smart_battery_chart_battery_capacity_fullname';
    chart.noDataText = noDataText;
    chart.tooltip = {
      label: "`${xsI18nPipe.transform('smart_battery_battery_capacity_tooltip_label', this.series.name)}`",
      labelArgs: ['smart_battery_battery_capacity_tooltip_label', 'this.series.name'],
      pastLabel: "`${xsI18nPipe.transform('smart_battery_battery_capacity_tooltip_label', this.series.name)}`",
      pastLabelArgs: ['smart_battery_battery_capacity_tooltip_label', 'this.series.name'],
      value:
        "`${xsI18nPipe.transform('smart_battery_battery_capacity_tooltip_value', this.y, (this.y/this.series.userOptions.count*100).toFixed(2), this.series.userOptions.count)}`",
      valueArgs: [
        'smart_battery_battery_capacity_tooltip_value',
        'this.y',
        'percentage(this.y, this.series.userOptions.count)',
        'this.series.userOptions.count',
      ],
      dateTime: 'this.x',
    };
    chart.metric = { metricFormat: null, metricId: 'y', label: 'battery_count_label', format: null };
    chart.childDimension = {
      dimensionId: 'filterIdx',
      label: 'common_date_label',
      noDataLabel: 'no_data_label',
    };
    chart.dimension = {
      dimensionId: 'cycles',
      label: 'smart_battery_chart_battery_capacity_dimension_label',
      noDataLabel: 'no_data_label',
    };
    chart.filter = { type: 'mapped', attribute: 'cycles', childAttribute: 'time' };

    chart.colors = [
      HighchartsConstant.defaultBucket.good,
      HighchartsConstant.defaultBucket.average,
      HighchartsConstant.defaultBucket.bad,
      HighchartsConstant.defaultBucket.nodata,
    ];

    chart.uiSettings = [
      {
        id: 'forecast',
        controlType: 'SELECTBOX',
        type: 'number',
        displayType: 'dropdown',
        name: 'smart_battery_charts_forecast_dropdown_name',
        options: [
          {
            value: 0,
            label: 'smart_battery_dropdown_current_month_label',
          },
          {
            value: 1,
            label: 'smart_battery_dropdown_current_month_and_next_1_month_label',
          },
          {
            value: 2,
            label: 'smart_battery_dropdown_current_month_and_next_2_month_label',
          },
          {
            value: 3,
            label: 'smart_battery_dropdown_current_month_and_next_3_month_label',
          },
        ],
      },
      {
        id: 'history',
        controlType: 'SELECTBOX',
        type: 'number',
        displayType: 'dropdown',
        name: 'smart_battery_charts_past_range_dropdown_name',
        options: [
          {
            value: 0,
            label: 'smart_battery_dropdown_current_month_label',
          },
          {
            value: 1,
            label: 'smart_battery_dropdown_current_month_and_previous_1_month_label',
          },
          {
            value: 2,
            label: 'smart_battery_dropdown_current_month_and_previous_2_month_label',
          },
          {
            value: 3,
            label: 'smart_battery_dropdown_current_month_and_previous_3_month_label',
          },
        ],
      },
      {
        id: 'batteryCapacity',
        controlType: 'THRESHOLD',
        type: 'number',
        displayType: 'dropdown',
        name: 'smart_battery_chart_battery_capacity_capacity_range_dropdown_message',
        options: [],
      },
      {
        id: 'predictionBeta',
        name: 'smart_battery_chart_health_prediction_engine_name',
        tooltip: 'smart_battery_chart_health_prediction_engine_tooltip',
        controlType: 'decal',
      },
    ];
  });

  patchChart(page.charts, SMART_BATTERY_CHARGE_LEVEL, (chart: BarChartConfig) => {
    chart.dimension = {
      dimensionId: 'label',
      label: 'battery_chart_chargelevel_dimension_label',
      noDataLabel: 'no_data_label',
    };
    chart.tag = {
      name: 'smart_battery_chart_charge_level_hour_name',
      parameters: ['hour'],
    };

    chart.filters = {
      hour: 8,
    };

    chart.metric = { metricFormat: null, metricId: 'y', label: 'battery_count_label', format: null };
    chart.integerOnly = true;
    chart.fixedColors = STARTING_CHARGE_LEVEL_COLORS;
    chart.name = 'smart_battery_chart_charge_level_hour_name';
    chart.fullName = 'smart_battery_chart_charge_level_hour_fullname';
    chart.reportSummary = 'smart_battery_chart_charge_level_hour_reportsummary';
    chart.noDataText = '{NO_DATA}';
    chart.tooltip = {
      label:
        "`${xsI18nPipe.transform('smart_battery_chart_battery_charge_level_hour_tooltip_label', xsI18nPipe.transform(this.point.hr), xsI18nPipe.transform(this.point.label))}`",
      labelArgs: [
        'smart_battery_chart_battery_charge_level_hour_tooltip_label',
        'xsI18nPipe.transform(this.point.hr)',
        'xsI18nPipe.transform(this.point.label)',
      ],
      value:
        "`${xsI18nPipe.transform('smart_battery_chart_battery_charge_level_hour_tooltip_value', this.point.y, ((this.point.y/this.point.batcount)*100).toFixed(2), this.point.batcount)}`",
      valueArgs: [
        'smart_battery_chart_battery_charge_level_hour_tooltip_value',
        'this.point.y',
        'percentage(this.point.y, this.point.batcount)',
        'this.point.batcount',
      ],
    };
    chart.uiSettings = [
      {
        id: 'hour',
        controlType: 'SELECTBOX',
        type: 'number',
        displayType: 'dropdown',
        options: [
          {
            value: 0,
            label: '0_hour',
          },
          {
            value: 1,
            label: '1_hour',
          },
          {
            value: 2,
            label: '2_hour',
          },
          {
            value: 3,
            label: '3_hour',
          },
          {
            value: 4,
            label: '4_hour',
          },
          {
            value: 5,
            label: '5_hour',
          },
          {
            value: 6,
            label: '6_hour',
          },
          {
            value: 7,
            label: '7_hour',
          },
          {
            value: 8,
            label: '8_hour',
          },
          {
            value: 9,
            label: '9_hour',
          },
          {
            value: 10,
            label: '10_hour',
          },
          {
            value: 11,
            label: '11_hour',
          },
          {
            value: 12,
            label: '12_hour',
          },
          {
            value: 13,
            label: '13_hour',
          },
          {
            value: 14,
            label: '14_hour',
          },
          {
            value: 15,
            label: '15_hour',
          },
          {
            value: 16,
            label: '16_hour',
          },
          {
            value: 17,
            label: '17_hour',
          },
          {
            value: 18,
            label: '18_hour',
          },
          {
            value: 19,
            label: '19_hour',
          },
          {
            value: 20,
            label: '20_hour',
          },
          {
            value: 21,
            label: '21_hour',
          },
          {
            value: 22,
            label: '22_hour',
          },
          {
            value: 23,
            label: '23_hour',
          },
        ],
      },
    ];
  });

  patchChart(page.charts, SMART_BATTERY_TABLE, (chart: TableConfig) => {
    chart.name = 'switcher_batteries_title';
    chart.cardStyle = ['bottomStatic', 'static'];
    chart.childTableKey = 'batteryId';
    chart.childTableTemplate = 'device-list';
    chart.selectionType = InsSelectionType.multiple;
    chart.selectionText = 'smart_battery_table_selected_batteries_label';
    chart.selectionTextPlural = 'smart_battery_table_selected_batteries_plural_label';
    chart.rowActions = [
      {
        action: 'decommission',
        tooltip: 'smart_battery_table_decommission_tooltip',
        icon: 'insight-icon-decommissionbattery',
      },
    ];

    chart.tableAttributes = [
      {
        id: 'mfgBatteryId',
        label: 'smart_battery_table_attribute_batteryId_label',
        renderer: TableRenderEnum.LINK,
        rendererParams: {
          eventId: 'batteryClicked',
          icons: [
            {
              iconField: 'isActive',
              iconCondition: true,
              iconClass: 'insight-icon-inusebattery',
              iconColor: '#2E86C1',
            },
            {
              iconField: 'isActive',
              iconCondition: false,
              iconClass: 'insight-icon-inusebattery',
              iconColor: '#FFA726',
            },
          ],
        },
      },
      {
        id: 'manufacturer',
        label: 'app_mapper_SmartBattery_Manufacturer',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'manufacturer',
      },
      {
        id: 'model',
        label: 'app_mapper_SmartBattery_Device_Model',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'model',
      },
      {
        id: 'serialNo',
        label: 'smart_battery_serial_number_name',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'serialNo',
      },
      {
        id: 'manufactureDate',
        label: 'app_mapper_SmartBattery_ManufactureDate',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'manufactureDate',
        rendererParams: {
          dateFormat: 'YYYY-MM-DD',
        },
      },
      {
        id: 'partNo',
        label: 'app_mapper_SmartBattery_PartNumber',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'partNo',
      },
      {
        id: 'devices',
        label: 'switcher_devices_title',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'devices',
      },
      {
        id: 'dischargeRate',
        label: 'avgdischargeRate_label',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'dischargeRate',
      },
      {
        id: 'capacity',
        label: 'remainingbatterycapacity_label',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'capacity',
      },
      {
        id: 'cycles',
        label: 'totalcycles_label',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'cycles',
      },
      {
        id: 'age',
        label: 'app_mapper_SmartBattery_Age_Months',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'age',
      },
      {
        id: 'batteryId',
        label: '',
        renderer: TableRenderEnum.NUMBER,
        iconClass: '',
        primaryColumnSortId: 'batteryId',
      },
      {
        id: 'lastPlugin',
        label: 'placedin_label',
        renderer: TableRenderEnum.DATE,
        iconClass: '',
        primaryColumnSortId: 'lastPlugin',
        rendererParams: {
          dateFormat: 'MMM DD YYYY [at] hh:mm A',
        },
      },
      {
        id: 'firstDetected',
        label: 'app_mapper_SmartBattery_FirstDetected',
        renderer: TableRenderEnum.DATE,
        iconClass: '',
        primaryColumnSortId: 'firstDetected',
        rendererParams: {
          dateFormat: 'MMM DD YYYY [at] hh:mm A',
        },
      },
      {
        id: 'deviceName',
        label: 'Column_Name_Device_Name',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'deviceName',
      },
    ];

    chart.uiParams = {
      ...chart.uiParams,
      columnsToRender: chart.tableAttributes.map(ta => ta.id).filter(ta => ta !== 'batteryId'),
      visibleColumns: chart.tableAttributes.map(ta => ta.id).filter(ta => ta !== 'batteryId'),
    };
  });
}

function patchSmartBatteryNewPage(page: PageConfigWithCharts) {
  page.filterBarVisible = true;
  page.filterBarConfig = {
    autoUpdate: false,
    andOnly: true,
    showReset: true,
    uniqueFields: true,
    fields: [
      {
        id: 'serialNo',
        name: 'smart_battery_serial_number_name',
        type: 'string',
        validation: FILTER_VALIDATION_NONEMPTY_SAFE_TEXT,
      },
      {
        id: 'mfgBatteryId',
        name: 'sb_spotlight_summary_battery_id_label',
        type: 'string',
        validation: FILTER_VALIDATION_NONEMPTY_SAFE_TEXT,
      },
      {
        id: 'manufacturer',
        name: 'smart_battery_manufacturer_name',
        type: 'string',
        validation: FILTER_VALIDATION_NONEMPTY_SAFE_TEXT,
      },
      {
        id: 'batteryPartNumber',
        name: 'smart_battery_part_number_name',
        type: 'string',
        validation: FILTER_VALIDATION_NONEMPTY_SAFE_TEXT,
      },
      { id: 'manufactureDate', name: 'smart_battery_manufacturer_date_label', type: 'date' },
      // { id: 'importDate', name: 'app_mapper_smart_Import_Date', type: 'date' },
    ],
  };

  patchChart(page.charts, 'sbTableUnused', (chart: TableConfig) => {
    chart.name = 'switcher_batteries_title';
    chart.selectionType = InsSelectionType.multiple;
    chart.selectionText = 'smart_battery_table_selected_batteries_label';
    chart.selectionTextPlural = 'smart_battery_table_selected_batteries_plural_label';
    chart.rowActions = [
      {
        action: 'delete',
        tooltip: 'xs_delete_label',
        icon: 'insight-icon-delete',
      },
    ];

    chart.tableAttributes = [
      {
        id: 'mfgBatteryId',
        label: 'smart_battery_table_attribute_batteryId_label',
        renderer: TableRenderEnum.ICON,
        iconClass: 'insight-icon-neverusedbattery new',
        primaryColumnSortId: 'mfgBatteryId',
      },
      {
        id: 'manufacturer',
        label: 'app_mapper_SmartBattery_Manufacturer',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'manufacturer',
      },
      {
        id: 'serialNo',
        label: 'smart_battery_serial_number_name',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'serialNo',
      },
      {
        id: 'manufactureDate',
        label: 'app_mapper_SmartBattery_ManufactureDate',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'manufactureDate',
        rendererParams: {
          dateFormat: 'YYYY-MM-DD',
        },
      },
      {
        id: 'partNo',
        label: 'app_mapper_SmartBattery_PartNumber',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'partNo',
      },
      {
        id: 'ratedCapacity',
        label: 'app_mapper_SmartBattery_RatedCapacity',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'ratedCapacity',
      },
      {
        id: 'importDate',
        label: 'app_mapper_smart_Import_Date',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'importDate',
      },
      {
        id: 'importedBy',
        label: 'importedBy_label',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'importedBy',
      },
    ];
  });
}

function patchSmartBatteryDecomPage(page: PageConfigWithCharts) {
  page.filterBarVisible = true;
  page.filterBarConfig = DEFAULT_BATTERY_FILTER_BAR_CONFIG;
  page.pageCategory = PageCategory.BATTERYSHIFT;

  patchChart(page.charts, 'sbTableDecom', (chart: TableConfig) => {
    chart.name = 'switcher_batteries_title';
    chart.childTableKey = 'batteryId';
    chart.childTableTemplate = 'device-list';
    chart.selectionType = InsSelectionType.multiple;
    chart.selectionText = 'smart_battery_table_selected_batteries_label';
    chart.selectionTextPlural = 'smart_battery_table_selected_batteries_plural_label';

    chart.rowActions = [
      {
        action: 'recommission',
        tooltip: 'app_mapper_smart_Recommission',
        icon: 'insight-icon-recommissionbattery',
      },
      {
        action: 'delete',
        tooltip: 'xs_delete_label',
        icon: 'insight-icon-delete',
      },
    ];
    chart.tableAttributes = [
      {
        id: 'mfgBatteryId',
        label: 'smart_battery_table_attribute_batteryId_label',
        renderer: TableRenderEnum.LINK,
        rendererParams: {
          eventId: 'batteryClicked',
          icons: [
            {
              iconClass: 'insight-icon-inactivesmartbattery removed',
            },
          ],
        },
      },
      {
        id: 'manufacturer',
        label: 'app_mapper_SmartBattery_Manufacturer',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'manufacturer',
      },
      {
        id: 'model',
        label: 'app_mapper_SmartBattery_Device_Model',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'model',
      },
      {
        id: 'serialNo',
        label: 'smart_battery_serial_number_name',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'serialNo',
      },
      {
        id: 'manufactureDate',
        label: 'app_mapper_SmartBattery_ManufactureDate',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'manufactureDate',
        rendererParams: {
          dateFormat: 'YYYY-MM-DD',
        },
      },
      {
        id: 'partNo',
        label: 'app_mapper_SmartBattery_PartNumber',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'partNo',
      },
      {
        id: 'devices',
        label: 'switcher_devices_title',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'devices',
      },
      {
        id: 'lastDeviceId',
        label: 'app_mapper_smart_Last_Device_ID',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'lastDeviceId',
      },
      {
        id: 'dischargeRate',
        label: 'avgdischargeRate_label',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'dischargeRate',
      },
      {
        id: 'capacity',
        label: 'remainingbatterycapacity_label',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'capacity',
      },
      {
        id: 'cycles',
        label: 'totalcycles_label',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'cycles',
      },
      {
        id: 'age',
        label: 'app_mapper_SmartBattery_Age_Months',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'age',
      },
      {
        id: 'batteryId',
        label: '',
        renderer: TableRenderEnum.NUMBER,
        iconClass: '',
        primaryColumnSortId: 'batteryId',
      },
      {
        id: 'decomissionedOn',
        label: 'datedecomissioned_label',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'decomissionedOn',
      },
    ];

    chart.uiParams = {
      ...chart.uiParams,
      columnsToRender: chart.tableAttributes.map(ta => ta.id).filter(ta => ta !== 'batteryId'),
      visibleColumns: chart.tableAttributes.map(ta => ta.id).filter(ta => ta !== 'batteryId'),
    };
  });
}

function patchSmartBatteryAllPage(page: PageConfigWithCharts) {
  page.pageCategory = PageCategory.BATTERYSHIFT;
  page.filterBarVisible = false;
  const noDataText = 'smart_battery_page_inventory_no_data_message';

  patchChart(page.charts, 'sbInvLastUpdated', (chart: BarChartConfig) => {
    chart.cardStyle = ['topStatic', 'static'];
  });

  patchChart(page.charts, 'sbInventory', (chart: BarChartConfig) => {
    chart.fullName = 'smart_battery_chart_inventory_fullname';
    chart.noDataText = noDataText;
    chart.dimension = {
      label: 'smart_battery_battery_status_name',
      dimensionId: 'name',
      noDataLabel: 'no_data_label',
    };
    chart.metric = { label: 'battery_count_label', metricId: 'metric' };
    chart.name = 'smart_battery_chart_inventory_name';
    chart.colorByPoint = true;
    chart.allowPointSelect = false;
    chart.integerOnly = true;
    chart.fixedColors = [
      { label: 'smart_battery_chart_inventory_New_colour_label', color: HighchartsConstant.batteryStatusBucket.New },
      {
        label: 'smart_battery_chart_inventory_Active_colour_label',
        color: HighchartsConstant.batteryStatusBucket.Active,
      },
      {
        label: 'smart_battery_chart_inventory_Inactive_colour_label',
        color: HighchartsConstant.batteryStatusBucket.Inactive,
      },
      {
        label: 'smart_battery_chart_inventory_Decommissioned_colour_label',
        color: HighchartsConstant.batteryStatusBucket.Decommissioned,
      },
    ];
    chart.showSummary = true;
    chart.tooltip = {
      label:
        "`${xsI18nPipe.transform('smart_battery_chart_inventory_tooltip_label',xsI18nPipe.transform(this.point.name))}`",
      labelArgs: ['smart_battery_chart_inventory_tooltip_label', 'xsI18nPipe.transform(this.point.name)'],
      value:
        "`${xsI18nPipe.transform('smart_battery_chart_inventory_tooltip_value', this.point.y, this.point.batcount, ((this.point.y/this.point.batcount)*100).toFixed(2))}`",
      valueArgs: [
        'smart_battery_chart_inventory_tooltip_value',
        'this.point.y',
        'this.point.batcount',
        'percentage(this.point.y, this.point.batcount)',
        'this.point.batcount',
      ],
    };
    chart.type = ChartTypeEnum.COLUMN;
  });

  patchChart(page.charts, 'sbInvParts', (chart: StackedDonutChartConfig) => {
    chart.fullName = 'smart_battery_chart_inventory_parts_fullName';
    chart.noDataText = noDataText;
    chart.type = ChartTypeEnum.STACKED_DONUT;
    chart.name = 'smart_battery_chart_inventory_parts_name';
    chart.dimension = {
      label: 'app_mapper_smart_dimension',
      dimensionId: 'label',
      noDataLabel: 'no_data_label',
    };
    chart.childDimension = {
      label: 'smart_battery_part_number_name',
      dimensionId: 'PartNo',
      noDataLabel: 'no_data_label',
    };
    chart.metric = { label: 'app_mapper_smart_metric_label', metricId: 'metric' };
    chart.tooltip = {
      label:
        "`${!!this.x? xsI18nPipe.transform('smart_battery_battery_status_name') : xsI18nPipe.transform('app_mapper_smart_Battery_Part')}: ${this.key}`",
      labelArgs: ['this.x', 'smart_battery_battery_status_name', 'app_mapper_smart_Battery_Part', 'this.key'],
      value:
        "`${xsI18nPipe.transform('smart_battery_chart_inventory_parts_tooltip_value', this.y, this.total, ((this.y/this.total)*100).toFixed(2))}`",
      valueArgs: [
        'smart_battery_chart_inventory_parts_tooltip_value',
        'this.y',
        'this.total',
        'percentage(this.point.y, this.point.total)',
      ],
    };
    // chart.childTooltip = {
    //   label: '{`${${}}: ${this.series.name}`;}',
    //   value:
    //     "`${xsI18nPipe.transform('smart_battery_chart_inventory_parts_tooltip_value', this.y, this.total, ((this.y/this.total)*100).toFixed(2))}`",
    // };
    chart.showSummary = true;
    chart.colorMap = {
      smart_battery_chart_inventory_Active_colour_label: HighchartsConstant.batteryStatusBucket.Active,
      smart_battery_chart_inventory_Inactive_colour_label: HighchartsConstant.batteryStatusBucket.Inactive,
      smart_battery_chart_inventory_New_colour_label: HighchartsConstant.batteryStatusBucket.New,
      smart_battery_chart_inventory_Decommissioned_colour_label: HighchartsConstant.batteryStatusBucket.Decommissioned,
    };
    chart.childColorMap = {
      //TODO move to soti colors when gradient are decided
      smart_battery_chart_inventory_Active_colour_label: HighchartsConstant.colorPalettes.stackedDonutActiveShades,
      smart_battery_chart_inventory_Inactive_colour_label: HighchartsConstant.colorPalettes.stackedDonutInactiveShades,
      smart_battery_chart_inventory_New_colour_label: HighchartsConstant.colorPalettes.stackedDonutNewShades,
      smart_battery_chart_inventory_Decommissioned_colour_label:
        HighchartsConstant.colorPalettes.stackedDonutDecommissionedShades,
    };
    chart.pieSize = 300;
    chart.allowPointSelect = false;
  });

  patchChart(page.charts, 'sbInvStatus', (chart: StackedDonutChartConfig) => {
    chart.fullName = 'smart_battery_chart_inventory_status_fullname';
    chart.noDataText = noDataText;
    chart.type = ChartTypeEnum.STACKED_DONUT;
    chart.dimension = {
      label: 'app_mapper_smart_dimension',
      dimensionId: 'label',
      noDataLabel: 'no_data_label',
    };
    chart.childDimension = {
      label: 'smart_battery_device_manufacturer_name',
      dimensionId: 'label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = { label: 'app_mapper_smart_metric_label', metricId: 'metric' };
    chart.tooltip = {
      label:
        "`${!!this.x? xsI18nPipe.transform('smart_battery_battery_status_name') : xsI18nPipe.transform('app_mapper_smart_Device')}: ${this.key}`",
      labelArgs: ['this.x', 'smart_battery_battery_status_name', 'app_mapper_smart_Device', 'this.key'],
      value:
        "`${xsI18nPipe.transform('smart_battery_chart_inventory_status_tooltip_value', this.y, this.total, ((this.y/this.total)*100).toFixed(2))}`",
      valueArgs: [
        'smart_battery_chart_inventory_status_tooltip_value',
        'this.y',
        'this.total',
        'percentage(this.point.y, this.point.total)',
      ],
    };
    chart.name = 'smart_battery_chart_inventory_status_name';
    chart.showSummary = true;
    chart.pieSize = 300;
    chart.allowPointSelect = false;
    chart.colorMap = {
      smart_battery_chart_inventory_Active_colour_label: HighchartsConstant.batteryStatusBucket.Active,
      smart_battery_chart_inventory_Inactive_colour_label: HighchartsConstant.batteryStatusBucket.Inactive,
      smart_battery_chart_inventory_New_colour_label: HighchartsConstant.batteryStatusBucket.New,
      smart_battery_chart_inventory_Decommissioned_colour_label: HighchartsConstant.batteryStatusBucket.Decommissioned,
    };
    chart.childColorMap = {
      smart_battery_chart_inventory_Active_colour_label: HighchartsConstant.colorPalettes.stackedDonutActiveShades,
    };
  });
}

export function patchSmartBatterySpotlight(page: PageConfigWithCharts) {
  page.name = '';
  page.pageCategory = PageCategory.BATTERYSHIFT;
  page.predefinedCharts = [
    {
      category: {
        name: 'app_mapper_smart_Summaries',
        icon: 'insight-icon-battery-usage',
        tooltip: 'app_mapper_smart_Summary_Information',
      },
      charts: [
        buildChartOptions(page.charts, 'sb-spotlight-summary', 'smart_battery_spotlight_battery_summary_name'),
        buildChartOptions(page.charts, 'sb-spotlight-device', 'smart_battery_spotlight_last_device_summary_name'),
      ],
    },
    {
      category: {
        name: 'app_mapper_smart_Battery_Health_Charts',
        icon: 'insight-icon-battery-usage',
        tooltip: 'app_mapper_smart_Metrics_tracked_over_time',
      },
      charts: [
        buildChartOptions(page.charts, 'sb-spotlight-capacity', 'smart_battery_spotlight_Remaining_Battery_Capacity'),
        buildChartOptions(page.charts, 'sb-spotlight-health', 'smart_battery_spotlight_chart_health_name'),
        buildChartOptions(page.charts, 'sb-spotlight-cycles', 'smart_battery_spotlight_chart_cycles_name'),
        buildChartOptions(page.charts, 'sbs-chargeTS', 'sdd_battery_level_ts_name'),
      ],
    },
  ];

  patchCard<TimelineChartConfig>(page.charts, 'sb-spotlight-capacity', chart => {
    chart.name = 'smart_battery_spotlight_chart_battery_capacity_name';
    chart.iconTooltip = 'app_mapper_smart_current_battery_capacity_tooltip';
    chart.chartHeight = 240;

    chart.tooltip = {
      label: 'sb_spotlight_ts_tooltip_date_label',
      value: 'smart_battery_spotlight_chart_battery_capacity_value_tooltip',
      valueArgs: ['app_mapper_smart_Estimated_Capacity'],
    };
    chart.dimension = {
      dimensionId: 'date',
      label: 'sb_spotlight_charge_level_dimension_label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = {
      metricId: 'capacity',
      metricMax: 'ratedCapacity',
      label: 'app_mapper_smart_Estimated_Capacity',
    };
    chart.multiTimelineMetric = [
      { metricId: 'capacity', metricMax: 'ratedCapacity', label: 'app_mapper_smart_Estimated_Capacity' },
    ];
    chart.timelineColor = '#CD4A76';
    chart.zoomType = '';
    chart.plotBand = true;
  });

  patchCard<TimelineChartConfig>(page.charts, 'sb-spotlight-health', chart => {
    chart.name = 'smart_battery_spotlight_chart_health_name';
    chart.iconTooltip = 'smart_battery_spotlight_chart_health_tooltip';
    chart.chartHeight = 240;

    chart.tooltip = {
      label: 'sb_spotlight_ts_tooltip_date_label',
      value: 'smart_battery_spotlight_chart_health_value_tooltip',
      valueArgs: ['app_mapper_smart_Estimated_Health'],
    };
    chart.dimension = {
      dimensionId: 'date',
      label: 'sb_spotlight_charge_level_dimension_label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = {
      metricId: 'health',
      label: 'app_mapper_smart_Estimated_Capacity_%',
    };
    chart.multiTimelineMetric = [{ metricId: 'health', label: 'app_mapper_smart_Estimated_Health' }];
    chart.timelineColor = '#CD4A76';
    chart.zoomType = '';
  });

  patchCard<TimelineChartConfig>(page.charts, 'sb-spotlight-cycles', chart => {
    chart.name = 'smart_battery_spotlight_chart_cycles_name';
    chart.iconTooltip = 'smart_battery_spotlight_chart_cycles_tooltip';
    chart.chartHeight = 240;
    chart.integerOnly = true;
    chart.tooltip = {
      label: 'sb_spotlight_ts_tooltip_date_label',
      value: 'smart_battery_spotlight_chart_cycles_value_tooltip',
    };
    chart.dimension = {
      dimensionId: 'date',
      label: 'sb_spotlight_charge_level_dimension_label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = {
      metricId: 'cycles',
      label: 'smart_battery_spotlight_charge_cycles_metric_label',
    };
    chart.multiTimelineMetric = [
      { metricId: 'cycles', label: 'smart_battery_spotlight_chart_cycles_multi_timeline_metric_label' },
    ];
    chart.timelineColor = '#CD4A76';
    chart.zoomType = '';
  });

  patchCard(page.charts, 'sb-spotlight-summary', card => {
    card.name = 'smart_battery_spotlight_battery_summary_name';
  });
  patchCard(page.charts, 'sb-spotlight-device', card => {
    card.name = 'smart_battery_spotlight_last_device_summary_name';
  });

  patchCard<TimelineChartConfig>(page.charts, 'sbs-chargeTS', chart => {
    chart.icon = 'insight-icon-moreaction';
    chart.iconTooltip = 'sb_spotlight_charge_level_ts_tooltip';
    chart.chartHeight = 240;
    chart.name = 'sb_spotlight_charge_level_ts_name';
    chart.integerOnly = true;

    chart.dimension = {
      dimensionId: 'date',
      label: 'sb_spotlight_charge_level_dimension_label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = {
      metricId: 'level',
      label: 'sb_spotlight_charge_level_ts_metric_label',
    };
    chart.tooltip = {
      label: 'sb_spotlight_ts_tooltip_date_label',
      value: 'sb_spotlight_charge_level_ts_tooltip_value',
    };
    chart.xMinRange = 1000 * 60 * 60;
    chart.legendDisabled = true;
    chart.yMax = 100;
  });
}

export function mapSmartBatteryConfig(
  app: ApplicationConfigWithPages,
  devApp: ApplicationConfigWithPages,
  devAppWin: ApplicationConfigWithPages
): ApplicationConfigWithPages {
  app.name = 'app_mapper_smart_Smart_Battery';
  app.dashboardType = CONSTANTS.DASHBOARD_TYPES.SMARTBATTERY;
  patchPage(app, SMART_BATTERY_PAGE, patchSmartBatteryPage);
  patchPage(app, SMART_BATTERY_NEWPAGE, patchSmartBatteryNewPage);
  patchPage(app, SMART_BATTERY_DECOMPAGE, patchSmartBatteryDecomPage);
  patchPage(app, SMART_BATTERY_ALLPAGE, patchSmartBatteryAllPage);

  if (app.pages.some(p => p.baselineId === SMART_BATTERY_SPOTLIGHT)) {
    patchPage(app, SMART_BATTERY_SPOTLIGHT, patchSmartBatterySpotlight);
  }

  const pageIds = [
    findPageId(app.pages, SMART_BATTERY_PAGE),
    findPageId(app.pages, SMART_BATTERY_DECOMPAGE),
    findPageId(app.pages, SMART_BATTERY_NEWPAGE),
    findPageId(app.pages, SMART_BATTERY_ALLPAGE),
  ];

  const childPageIds = [
    findPageId(devApp.pages, SMART_BATTERY_SPOTLIGHT),
    findPageId(devApp.pages, DEVICE_BATTERY_USAGE),
    findPageId(devAppWin.pages, DEVICE_BATTERY_USAGE_W),
    findPageId(devApp.pages, DEVICE_APP_USAGE),
    findPageId(devApp.pages, DEVICE_WEBAPP_USAGE),
    findPageId(devApp.pages, DEVICE_DATA_USAGE),
    findPageId(devAppWin.pages, DEVICE_DATA_USAGE_W),
    findPageId(devApp.pages, DEVICE_LAST_KNOWN),
    findPageId(devApp.pages, DEVICE_SIGNAL_STRENGTH),
    findPageId(devAppWin.pages, DEVICE_SIGNAL_STRENGTH_W),
    findPageId(devApp.pages, DEVICE_WIFI),
  ];

  const defaultTabId = findPageId(devApp.pages, SMART_BATTERY_SPOTLIGHT);

  return { ...app, pageIds, childPageIds, defaultTabId };
}
